/**
 * PrivilegesPage.jsx — set per-user access to actors/crops/objectives/complexity
 */
import { useEffect, useState } from "react";
import { adminGetUsers, adminGetPrivileges, adminSetPrivileges, fetchWizardOptions } from "../../services/api";

export default function PrivilegesPage() {
  const [users,    setUsers]    = useState([]);
  const [options,  setOptions]  = useState(null);
  const [selected, setSelected] = useState(null);
  const [privs,    setPrivs]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState({ text: "", ok: true });

  useEffect(() => {
    Promise.all([adminGetUsers(), fetchWizardOptions()])
      .then(([u, o]) => {
        setUsers(u.filter((x) => x.role !== "admin"));
        setOptions(o);
      })
      .catch((e) => setMsg({ text: e.message, ok: false }))
      .finally(() => setLoading(false));
  }, []);

  async function selectUser(userId) {
    setSelected(userId); setMsg({ text: "", ok: true }); setPrivs(null);
    try {
      const p = await adminGetPrivileges(userId);
      setPrivs({
        allowed_actors:     p.allowed_actors     ?? [],
        allowed_crops:      p.allowed_crops      ?? [],
        allowed_objectives: p.allowed_objectives ?? [],
        allowed_complexity: p.allowed_complexity ?? [],
      });
    } catch (e) { setMsg({ text: e.message, ok: false }); }
  }

  function toggle(field, value) {
    setPrivs((prev) => {
      const list = prev[field];
      return { ...prev, [field]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
    });
  }

  async function save() {
    setSaving(true); setMsg({ text: "", ok: true });
    try {
      await adminSetPrivileges(selected, privs);
      setMsg({ text: "Privileges saved.", ok: true });
    } catch (e) { setMsg({ text: e.message, ok: false }); }
    finally { setSaving(false); }
  }

  const FIELDS = [
    { key: "allowed_actors",     label: "Allowed actors",     opts: options?.actors     ?? [] },
    { key: "allowed_crops",      label: "Allowed crops",      opts: options?.crops      ?? [] },
    { key: "allowed_objectives", label: "Allowed objectives", opts: options?.objectives ?? [] },
    { key: "allowed_complexity", label: "Allowed complexity", opts: options?.complexity ?? [] },
  ];

  return (
    <div className="p-4" style={{ maxWidth: 900 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: "1.5rem" }}>Privileges</h2>
      {msg.text && (
        <div className={`alert py-2 mb-3 ${msg.ok ? "alert-success" : "alert-danger"}`}
             style={{ fontSize: 13 }}>{msg.text}</div>
      )}
      {loading ? <div className="text-muted" style={{ fontSize: 14 }}>Loading…</div> : (
        <div className="d-flex gap-4">
          {/* User list */}
          <div style={{ width: 220, flexShrink: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#6c757d", marginBottom: 8,
              textTransform: "uppercase", letterSpacing: "0.05em" }}>Select user</div>
            {users.length === 0 && <div style={{ fontSize: 13, color: "#adb5bd" }}>No regular users yet.</div>}
            {users.map((u) => (
              <button key={u.id} onClick={() => selectUser(u.id)} style={{
                display: "block", width: "100%", textAlign: "left",
                background: selected === u.id ? "#E6F1FB" : "transparent",
                border: "none", borderLeft: `3px solid ${selected === u.id ? "#185FA5" : "transparent"}`,
                padding: "8px 12px", fontSize: 13, cursor: "pointer",
                color: selected === u.id ? "#185FA5" : "#212529",
                borderRadius: "0 6px 6px 0", marginBottom: 2,
              }}>
                <div style={{ fontWeight: 500 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: "#6c757d" }}>{u.email}</div>
              </button>
            ))}
          </div>

          {/* Privileges panel */}
          <div style={{ flex: 1 }}>
            {!selected && (
              <div style={{ color: "#6c757d", fontSize: 13, paddingTop: "2rem" }}>
                Select a user on the left to manage their privileges.
              </div>
            )}
            {selected && !privs && <div style={{ fontSize: 13, color: "#adb5bd" }}>Loading…</div>}
            {selected && privs && (
              <div style={{ background: "#fff", border: "1px solid #dee2e6",
                borderRadius: 12, padding: "1.5rem" }}>
                {FIELDS.map(({ key, label, opts }) => {
                  const all = privs[key].length === 0;
                  return (
                    <div key={key} className="mb-4">
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                        {label}
                        <span style={{ fontWeight: 400, color: "#adb5bd", marginLeft: 6, fontSize: 12 }}>
                          {all ? "(all allowed)" : `(${privs[key].length} selected)`}
                        </span>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <button type="button" onClick={() => setPrivs((p) => ({ ...p, [key]: [] }))}
                          style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, cursor: "pointer",
                            border: `1px solid ${all ? "#185FA5" : "#dee2e6"}`,
                            background: all ? "#E6F1FB" : "#fff",
                            color: all ? "#185FA5" : "#6c757d", fontWeight: all ? 600 : 400 }}>
                          All
                        </button>
                        {opts.map((opt) => {
                          const val = opt.id ?? opt;
                          const on  = privs[key].includes(val);
                          return (
                            <button key={val} type="button" onClick={() => toggle(key, val)}
                              style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, cursor: "pointer",
                                border: `1px solid ${on ? "#185FA5" : "#dee2e6"}`,
                                background: on ? "#E6F1FB" : "#fff",
                                color: on ? "#185FA5" : "#495057", fontWeight: on ? 600 : 400 }}>
                              {opt.name ?? opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <button className="btn btn-primary btn-sm px-4" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save privileges"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
